import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageContext";
// framer-motion is now only loaded by lazy routes that need it (sticky CTAs,
// outbound landing). The LazyMotion provider is rendered inside those routes
// to keep ~80 KiB of vendor JS out of the homepage bundle.
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import RouteMetaSync from "./components/RouteMetaSync";

const Impressum = lazy(() => import("./pages/Impressum"));
const MarketingBestaetigung = lazy(() => import("./pages/MarketingBestaetigung"));
const Zossen = lazy(() => import("./pages/investments/Zossen"));
const JohannSigismund = lazy(() => import("./pages/investments/JohannSigismund"));
const Kaiserstrasse = lazy(() => import("./pages/investments/Kaiserstrasse"));
const KarlsLiving = lazy(() => import("./pages/investments/KarlsLiving"));
const KarlsLivingCooperation = lazy(() => import("./pages/cooperations/KarlsLivingCooperation"));
const KarlsLivingCooperationPreview = import.meta.env.DEV
  ? lazy(() => import("./pages/cooperations/KarlsLivingCooperationPreview"))
  : null;
const LudwigStrategie = lazy(() => import("./pages/investments/LudwigStrategie"));
// WEB-15: Briefing fail-closed deaktiviert (Payload noch nicht serverseitig).
const BriefingUnavailable = lazy(() => import("./pages/investments/BriefingUnavailable"));
const JessicaStrategie = lazy(() => import("./pages/investments/JessicaStrategie"));
const UrbanUnits = lazy(() => import("./pages/investments/UrbanUnits"));
const KrampnitzCarree = lazy(() => import("./pages/investments/KrampnitzCarree"));
const Calculator = lazy(() => import("./pages/Calculator"));
const CalculatorRedirect = lazy(() => import("./pages/CalculatorRedirect"));
const DealRoomDemoRedirect = lazy(() => import("./pages/DealRoomDemoRedirect"));
const GlossaryRedirect = lazy(() => import("./pages/GlossaryRedirect"));
const Ressourcen = lazy(() => import("./pages/Ressourcen"));
const Beratung = lazy(() => import("./pages/Beratung"));
const ImmobilienInvestment = lazy(() => import("./pages/ImmobilienInvestment"));
const Wissen = lazy(() => import("./pages/Wissen"));
const WohnungsleerstandBrandenburg2025Article = lazy(() => import("./pages/wissen/WohnungsleerstandBrandenburg2025Article"));
const Kapitalpuffer2027BauzinsenArticle = lazy(() => import("./pages/wissen/Kapitalpuffer2027BauzinsenArticle"));
const BerlinUmlandKaufpreisvorteilPendelkosten2026Article = lazy(() => import("./pages/wissen/BerlinUmlandKaufpreisvorteilPendelkosten2026Article"));
const ImmobilienpreisindexQ32026Article = lazy(() => import("./pages/wissen/ImmobilienpreisindexQ32026Article"));
const BevoelkerungsprognoseBerlinBrandenburg2045Article = lazy(() => import("./pages/wissen/BevoelkerungsprognoseBerlinBrandenburg2045Article"));
const LeerstandsprognoseArticle = lazy(() => import("./pages/wissen/LeerstandsprognoseArticle"));
const PreisprognoseArticle = lazy(() => import("./pages/wissen/PreisprognoseArticle"));
const WohnatlasArticle = lazy(() => import("./pages/wissen/WohnatlasArticle"));
const NeubauLeitfadenArticle = lazy(() => import("./pages/wissen/NeubauLeitfadenArticle"));
const DenkmalimmobilienArticle = lazy(() => import("./pages/wissen/DenkmalimmobilienArticle"));
const RestnutzungsdauerArticle = lazy(() => import("./pages/wissen/RestnutzungsdauerArticle"));
const BestandsleitfadenArticle = lazy(() => import("./pages/wissen/BestandsleitfadenArticle"));
const ZveSteuerzoneArticle = lazy(() => import("./pages/wissen/ZveSteuerzoneArticle"));
const KfwFoerderungArticle = lazy(() => import("./pages/wissen/KfwFoerderungArticle"));
const Kfw261DetailArticle = lazy(() => import("./pages/wissen/Kfw261DetailArticle"));
const Kfw297DetailArticle = lazy(() => import("./pages/wissen/Kfw297DetailArticle"));
const MietrechtsreformArticle = lazy(() => import("./pages/wissen/MietrechtsreformArticle"));
const MietpreisbremseArticle = lazy(() => import("./pages/wissen/MietpreisbremseArticle"));
const MilieuschutzArticle = lazy(() => import("./pages/wissen/MilieuschutzArticle"));
const MilieuschutzBerlinAtlasArticle = lazy(() => import("./pages/wissen/MilieuschutzBerlinAtlasArticle"));
const MarktanalyseArticle = lazy(() => import("./pages/wissen/MarktanalyseArticle"));
const AfaLeitfadenArticle = lazy(() => import("./pages/wissen/AfaLeitfadenArticle"));
const AfaDeepDiveArticle = lazy(() => import("./pages/wissen/AfaDeepDiveArticle"));
const DenkmalWiederverkaufArticle = lazy(() => import("./pages/wissen/DenkmalWiederverkaufArticle"));
const WohnmarktreportArticle = lazy(() => import("./pages/wissen/WohnmarktreportArticle"));
const MieteinnahmenSteuernArticle = lazy(() => import("./pages/wissen/MieteinnahmenSteuernArticle"));
const LohnsteuerermaessigungArticle = lazy(() => import("./pages/wissen/LohnsteuerermaessigungArticle"));
const EigenkapitalFinanzierungArticle = lazy(() => import("./pages/wissen/EigenkapitalFinanzierungArticle"));
const EigenkapitalFalle2026Article = lazy(() => import("./pages/wissen/EigenkapitalFalle2026Article"));
const SpekulationsfristArticle = lazy(() => import("./pages/wissen/SpekulationsfristArticle"));
const VvGmbhArticle = lazy(() => import("./pages/wissen/VvGmbhArticle"));
const SanierungskostenSteuernArticle = lazy(() => import("./pages/wissen/SanierungskostenSteuernArticle"));
const DoppelterSteuerhebelArticle = lazy(() => import("./pages/wissen/DoppelterSteuerhebelArticle"));
const GrunderwerbsteuerArticle = lazy(() => import("./pages/wissen/GrunderwerbsteuerArticle"));
const MabvSchildArticle = lazy(() => import("./pages/wissen/MabvSchildArticle"));
const MietvertragsgestaltungArticle = lazy(() => import("./pages/wissen/MietvertragsgestaltungArticle"));
const BzaKfwArticle = lazy(() => import("./pages/wissen/BzaKfwArticle"));
const QuadratmeterKompendiumArticle = lazy(() => import("./pages/wissen/QuadratmeterKompendiumArticle"));
const PendlerachsenArticle = lazy(() => import("./pages/wissen/PendlerachsenArticle"));
const PreisrankingQ12026Article = lazy(() => import("./pages/wissen/PreisrankingQ12026Article"));
const PreisrankingQ22026Article = lazy(() => import("./pages/wissen/PreisrankingQ22026Article"));
const PwcRealEstateMonitorQ22026Article = lazy(() => import("./pages/wissen/PwcRealEstateMonitorQ22026Article"));
const MietrenditeAtlas2026Article = lazy(() => import("./pages/wissen/MietrenditeAtlas2026Article"));
const WohnatlasBriefing2026Article = lazy(() => import("./pages/wissen/WohnatlasBriefing2026Article"));
const WohnatlasVervielfaeltigerArticle = lazy(() => import("./pages/wissen/WohnatlasVervielfaeltigerArticle"));
const WohnbarometerMetropolen2026Article = lazy(() => import("./pages/wissen/WohnbarometerMetropolen2026Article"));
const BerlinBrandenburgGewinnerregionArticle = lazy(() => import("./pages/wissen/BerlinBrandenburgGewinnerregionArticle"));
const MietspiegelBerlin2026Article = lazy(() => import("./pages/wissen/MietspiegelBerlin2026Article"));
const NotarterminArticle = lazy(() => import("./pages/wissen/NotarterminArticle"));
const WegSevVerwaltungArticle = lazy(() => import("./pages/wissen/WegSevVerwaltungArticle"));
const GrundbuchTeil1Article = lazy(() => import("./pages/wissen/GrundbuchTeil1Article"));
const GrundbuchTeil2Article = lazy(() => import("./pages/wissen/GrundbuchTeil2Article"));
const GrundbuchTeil3Article = lazy(() => import("./pages/wissen/GrundbuchTeil3Article"));
const NeubauFinanzierungBauphaseArticle = lazy(() => import("./pages/wissen/NeubauFinanzierungBauphaseArticle"));
const GrundbuchNeubauTeil1Article = lazy(() => import("./pages/wissen/GrundbuchNeubauTeil1Article"));
const GrundbuchNeubauTeil2Article = lazy(() => import("./pages/wissen/GrundbuchNeubauTeil2Article"));
const GrundbuchNeubauTeil3Article = lazy(() => import("./pages/wissen/GrundbuchNeubauTeil3Article"));
const EinfamilienhausSpeckguertelArticle = lazy(() => import("./pages/wissen/EinfamilienhausSpeckguertelArticle"));
const ImmobilienKapitalanlageLeitfadenArticle = lazy(() => import("./pages/wissen/ImmobilienKapitalanlageLeitfadenArticle"));
const DirektVsCrowdArticle = lazy(() => import("./pages/wissen/DirektVsCrowdArticle"));
const ImmobilienrenditeBerechnenArticle = lazy(() => import("./pages/wissen/ImmobilienrenditeBerechnenArticle"));
const CashflowImmobilieArticle = lazy(() => import("./pages/wissen/CashflowImmobilieArticle"));
const SteuerAbsetzenArticle = lazy(() => import("./pages/wissen/SteuerAbsetzenArticle"));
const HauskaufSteuerAbsetzenArticle = lazy(() => import("./pages/wissen/HauskaufSteuerAbsetzenArticle"));
const AfaVermietungArticle = lazy(() => import("./pages/wissen/AfaImmobilieVermietungArticle"));
const MaklervereinbarungNdaArticle = lazy(() => import("./pages/wissen/MaklervereinbarungNdaArticle"));
const RisikenImmobilieninvestmentArticle = lazy(() => import("./pages/wissen/RisikenImmobilieninvestmentArticle"));
const KaufpreisaufteilungArticle = lazy(() => import("./pages/wissen/KaufpreisaufteilungArticle"));
const MehrfamilienhausKapitalanlageRenditeArticle = lazy(() => import("./pages/wissen/MehrfamilienhausKapitalanlageRenditeArticle"));
const CaseEnterpriseAeBerlinArticle = lazy(() => import("./pages/wissen/CaseEnterpriseAeBerlinArticle"));
const CaseHeadOfMarketingBerlinWerthebelArticle = lazy(() => import("./pages/wissen/CaseHeadOfMarketingBerlinWerthebelArticle"));
const CaseExpatSoftwareEngineerBerlinDoppelAfaArticle = lazy(() => import("./pages/wissen/CaseExpatSoftwareEngineerBerlinDoppelAfaArticle"));
const NeubauIllusion2026Article = lazy(() => import("./pages/wissen/NeubauIllusion2026Article"));
const KapitalanlageBerlin = lazy(() => import("./pages/KapitalanlageBerlin"));
const BezirkPage = lazy(() => import("./pages/BezirkPage"));
const KapitalanlageBrandenburg = lazy(() => import("./pages/KapitalanlageBrandenburg"));
const OutboundLanding = lazy(() => import("./pages/OutboundLanding"));
const PortfolioOverview = lazy(() => import("./pages/PortfolioOverview"));
const DealRoomDemo = lazy(() => import("./pages/DealRoomDemo"));
const Faq = lazy(() => import("./pages/Faq"));
const Glossar = lazy(() => import("./pages/Glossar"));
const GlossarTerm = lazy(() => import("./pages/GlossarTerm"));
const Empfehlungsprogramm = lazy(() => import("./pages/Empfehlungsprogramm"));
const HubPage = lazy(() => import("./pages/HubPage"));
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

const AdminContentAgent = lazy(() => import("./pages/admin/ContentAgent"));
const NewsletterBestaetigt = lazy(() => import("./pages/NewsletterBestaetigt"));
const NewsletterAbmelden = lazy(() => import("./pages/NewsletterAbmelden"));
const NewsletterArchive = lazy(() => import("./pages/NewsletterArchive"));
const NewsletterIndex = lazy(() => import("./pages/NewsletterIndex"));
const ArchitekturBriefLanding = lazy(() => import("./pages/ArchitekturBriefLanding"));

const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

import { AuthProvider } from "@/hooks/use-auth";
import { AdminGuard } from "@/components/admin/AdminGuard";

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
    <LanguageProvider>
      
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <RouteMetaSync />
          <Suspense
            fallback={
              <div
                className="min-h-screen w-full bg-background"
                role="status"
                aria-label="Lädt"
              />
            }
          >
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/marketing-bestaetigung" element={<MarketingBestaetigung />} />
              <Route path="/marketing-bestaetigt" element={<MarketingBestaetigung />} />
              <Route path="/marketing-confirmed" element={<MarketingBestaetigung />} />
              <Route path="/investments/zossen" element={<Zossen />} />
              <Route path="/investments/johann-sigismund" element={<JohannSigismund />} />
              <Route path="/investments/kaiserstrasse" element={<Kaiserstrasse />} />
              <Route path="/investments/karls-living" element={<KarlsLiving />} />
              <Route path="/investments/ludwig" element={<LudwigStrategie />} />
              <Route path="/investments/jessica" element={<JessicaStrategie />} />
              <Route path="/investments/ben-rohloff" element={<BriefingUnavailable />} />
              <Route path="/investments/ben-roloff" element={<Navigate to="/investments/ben-rohloff" replace />} />
              <Route path="/investments/urban-units" element={<UrbanUnits />} />
              <Route path="/investments/krampnitz-carree" element={<KrampnitzCarree />} />
              <Route path="/kooperationen/karls-living" element={<KarlsLivingCooperation />} />
              {KarlsLivingCooperationPreview ? (
                <Route
                  path="/kooperationen/karls-living/vorschau"
                  element={<KarlsLivingCooperationPreview />}
                />
              ) : null}
              <Route path="/calculator" element={<CalculatorRedirect />} />
              <Route path="/ressourcen" element={<Ressourcen />} />
              <Route path="/investitionsrechner" element={<Calculator />} />
              <Route path="/ressourcen/steuerhebel-rechner" element={<CalculatorRedirect />} />
              <Route path="/beratung" element={<Beratung />} />
              <Route path="/immobilien-investment" element={<ImmobilienInvestment />} />
              <Route path="/wissen" element={<Wissen />} />
              <Route path="/wissen/berlin-umland-kaufpreisvorteil-pendelkosten-2026" element={<BerlinUmlandKaufpreisvorteilPendelkosten2026Article />} />
              <Route path="/wissen/bevoelkerungsprognose-berlin-brandenburg-2045" element={<BevoelkerungsprognoseBerlinBrandenburg2045Article />} />
              <Route path="/wissen/leerstandsprognose-berlin-2045" element={<LeerstandsprognoseArticle />} />
              <Route path="/wissen/preisprognose-berlin-2035" element={<PreisprognoseArticle />} />
              <Route path="/wissen/wohnatlas-gewinnerregionen-2035" element={<WohnatlasArticle />} />
              <Route path="/wissen/wohnatlas-2026-briefing-berliner-umland" element={<WohnatlasBriefing2026Article />} />
              <Route path="/wissen/wohnatlas-2026-vervielfaeltiger-brandenburg" element={<WohnatlasVervielfaeltigerArticle />} />
              <Route path="/wissen/wohnbarometer-2026-metropolen-berlin-brandenburg" element={<WohnbarometerMetropolen2026Article />} />
              <Route path="/wissen/neubau-foerderung-afa-leitfaden" element={<NeubauLeitfadenArticle />} />
              <Route path="/wissen/denkmalimmobilien-kapitalanlage" element={<DenkmalimmobilienArticle />} />
              <Route path="/wissen/restnutzungsdauer-gutachten" element={<RestnutzungsdauerArticle />} />
              <Route path="/wissen/bestandsimmobilien-kapitalanlage" element={<BestandsleitfadenArticle />} />
              <Route path="/wissen/zve-steuerzonen-investoren" element={<ZveSteuerzoneArticle />} />
              <Route path="/wissen/kfw-foerderung-kapitalanleger" element={<KfwFoerderungArticle />} />
              <Route path="/wissen/kfw-261-wohngebaeude-kredit" element={<Kfw261DetailArticle />} />
              <Route path="/wissen/kfw-297-298-klimafreundlicher-neubau" element={<Kfw297DetailArticle />} />
              <Route path="/wissen/mietrechtsreform-2026" element={<MietrechtsreformArticle />} />
              <Route path="/wissen/mietpreisbremse" element={<MietpreisbremseArticle />} />
              <Route path="/wissen/milieuschutz" element={<MilieuschutzArticle />} />
              <Route path="/wissen/milieuschutz-berlin-bezirks-atlas" element={<MilieuschutzBerlinAtlasArticle />} />
              <Route path="/wissen/marktanalyse-berlin-brandenburg-2026" element={<MarktanalyseArticle />} />
              <Route path="/wissen/immobilienpreisindex-q3-2026-berlin-brandenburg" element={<ImmobilienpreisindexQ32026Article />} />
              <Route path="/wissen/afa-abschreibung-leitfaden" element={<AfaLeitfadenArticle />} />
              <Route path="/wissen/afa-deep-dive-denkmal-neubau" element={<AfaDeepDiveArticle />} />
              <Route path="/wissen/denkmalimmobilien-wiederverkauf" element={<DenkmalWiederverkaufArticle />} />
              <Route path="/wissen/wohnmarktreport-berlin-2026" element={<WohnmarktreportArticle />} />
              <Route path="/wissen/mieteinnahmen-steuern-kapitalanleger" element={<MieteinnahmenSteuernArticle />} />
              <Route path="/wissen/lohnsteuerermaessigung-immobilieninvestoren" element={<LohnsteuerermaessigungArticle />} />
              <Route path="/wissen/eigenkapital-finanzierung-investor" element={<EigenkapitalFinanzierungArticle />} />
              <Route path="/wissen/eigenkapital-falle-2026-hauskauf-vs-kapitalanlage" element={<EigenkapitalFalle2026Article />} />
              <Route path="/wissen/spekulationsfrist-immobilien" element={<SpekulationsfristArticle />} />
              <Route path="/wissen/vermoegensverwaltende-gmbh-vs-privatbesitz" element={<VvGmbhArticle />} />
              <Route path="/wissen/sanierungskosten-steuern-sofortabzug" element={<SanierungskostenSteuernArticle />} />
              <Route path="/wissen/doppelter-steuerhebel-bestand" element={<DoppelterSteuerhebelArticle />} />
              <Route path="/wissen/grunderwerbsteuer-kapitalanleger" element={<GrunderwerbsteuerArticle />} />
              <Route path="/wissen/mabv-schutzschild-neubau" element={<MabvSchildArticle />} />
              <Route path="/wissen/mietvertragsgestaltung-mindestmietdauer" element={<MietvertragsgestaltungArticle />} />
              <Route path="/wissen/bza-kfw-foerderung-neubau" element={<BzaKfwArticle />} />
              <Route path="/wissen/quadratmeter-flaechen-sonder-afa" element={<QuadratmeterKompendiumArticle />} />
              <Route path="/wissen/pendlerachsen-re-linien-brandenburg" element={<PendlerachsenArticle />} />
              <Route path="/wissen/preisranking-q1-2026-berlin-brandenburg" element={<PreisrankingQ12026Article />} />
              <Route path="/wissen/preisranking-q2-2026-bestand-vs-neubau" element={<PreisrankingQ22026Article />} />
              <Route path="/wissen/pwc-real-estate-monitor-q2-2026" element={<PwcRealEstateMonitorQ22026Article />} />
              <Route path="/wissen/mietrendite-atlas-2026-risikopraemie" element={<MietrenditeAtlas2026Article />} />
              <Route path="/wissen/berlin-brandenburg-gewinnerregion" element={<BerlinBrandenburgGewinnerregionArticle />} />
              <Route path="/wissen/mietspiegel-berlin-2026" element={<MietspiegelBerlin2026Article />} />
              <Route path="/wissen/notartermin-immobilienkauf" element={<NotarterminArticle />} />
              <Route path="/wissen/weg-sev-verwaltung" element={<WegSevVerwaltungArticle />} />
              <Route path="/wissen/grundbuch-aufbau-eigentum" element={<GrundbuchTeil1Article />} />
              <Route path="/wissen/grundbuch-abteilung-2-lasten" element={<GrundbuchTeil2Article />} />
              <Route path="/wissen/grundbuch-abteilung-3-grundpfandrechte" element={<GrundbuchTeil3Article />} />
              <Route path="/wissen/neubau-finanzierung-bauphase-leitfaden" element={<NeubauFinanzierungBauphaseArticle />} />
              <Route path="/wissen/grundbuch-neubau-teil-1-fundament" element={<GrundbuchNeubauTeil1Article />} />
              <Route path="/wissen/grundbuch-neubau-teil-2-grundschuld-mabv" element={<GrundbuchNeubauTeil2Article />} />
              <Route path="/wissen/grundbuch-neubau-teil-3-umschreibung" element={<GrundbuchNeubauTeil3Article />} />
              <Route path="/wissen/einfamilienhaus-speckguertel-kapitalanlage" element={<EinfamilienhausSpeckguertelArticle />} />
              <Route path="/wissen/immobilien-als-kapitalanlage-leitfaden" element={<ImmobilienKapitalanlageLeitfadenArticle />} />
              <Route path="/wissen/direktinvestment-vs-crowdinvesting" element={<DirektVsCrowdArticle />} />
              <Route path="/wissen/immobilienrendite-berechnen" element={<ImmobilienrenditeBerechnenArticle />} />
              <Route path="/wissen/cashflow-immobilie-berechnen" element={<CashflowImmobilieArticle />} />
              <Route path="/wissen/immobilie-steuerlich-absetzen" element={<SteuerAbsetzenArticle />} />
              <Route path="/wissen/hauskauf-steuer-absetzen" element={<HauskaufSteuerAbsetzenArticle />} />
              <Route path="/wissen/afa-immobilie-vermietung" element={<AfaVermietungArticle />} />
              <Route path="/wissen/maklervereinbarung-nda-off-market" element={<MaklervereinbarungNdaArticle />} />
              <Route path="/wissen/wohnungsleerstand-brandenburg-2025" element={<WohnungsleerstandBrandenburg2025Article />} />
              <Route path="/wissen/kapitalpuffer-2027-bauzinsen" element={<Kapitalpuffer2027BauzinsenArticle />} />
              <Route path="/wissen/risiken-immobilieninvestment" element={<RisikenImmobilieninvestmentArticle />} />
              <Route path="/wissen/kaufpreisaufteilung-grund-boden" element={<KaufpreisaufteilungArticle />} />
              <Route path="/wissen/mehrfamilienhaus-kapitalanlage-rendite" element={<MehrfamilienhausKapitalanlageRenditeArticle />} />
              <Route path="/wissen/case-study-enterprise-ae-berlin" element={<CaseEnterpriseAeBerlinArticle />} />
              <Route path="/wissen/case-head-of-marketing-berlin-werthebel" element={<CaseHeadOfMarketingBerlinWerthebelArticle />} />
              <Route path="/wissen/case-expat-software-engineer-berlin-doppel-afa" element={<CaseExpatSoftwareEngineerBerlinDoppelAfaArticle />} />
              <Route path="/wissen/neubau-illusion-2026" element={<NeubauIllusion2026Article />} />
              <Route path="/kapitalanlage/berlin" element={<KapitalanlageBerlin />} />
              <Route path="/kapitalanlage/berlin/:bezirk" element={<BezirkPage />} />
              <Route path="/kapitalanlage/brandenburg" element={<KapitalanlageBrandenburg />} />
              <Route path="/go/vermoegensarchitektur" element={<OutboundLanding />} />
              <Route path="/portfolio-overview" element={<PortfolioOverview />} />
              <Route path="/plattform" element={<DealRoomDemo />} />
              <Route path="/deal-room-demo" element={<DealRoomDemoRedirect />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/glossar" element={<Glossar />} />
              <Route path="/glossar/:slug" element={<GlossarTerm />} />
              {/* Legacy EN glossary tree → redirects to the canonical /glossar/* */}
              <Route path="/glossary" element={<GlossaryRedirect />} />
              <Route path="/glossary/:slug" element={<GlossaryRedirect />} />

              <Route path="/empfehlungsprogramm" element={<Empfehlungsprogramm />} />
              <Route path="/themen/:slug" element={<HubPage />} />

              <Route path="/newsletter/bestaetigt" element={<NewsletterBestaetigt />} />
              <Route path="/newsletter/confirmed" element={<NewsletterBestaetigt />} />
              <Route path="/newsletter/abmelden" element={<NewsletterAbmelden />} />
              <Route path="/newsletter/unsubscribe" element={<NewsletterAbmelden />} />
              <Route path="/newsletter" element={<NewsletterIndex />} />
              <Route path="/newsletter/:slug" element={<NewsletterArchive />} />
              <Route path="/architektur-brief" element={<ArchitekturBriefLanding />} />
              <Route path="/architecture-brief" element={<ArchitekturBriefLanding />} />

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />

              <Route
                path="/admin"
                element={
                  <AdminGuard>
                    <AdminContentAgent />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/content-agent"
                element={
                  <AdminGuard>
                    <AdminContentAgent />
                  </AdminGuard>
                }
              />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
      
    </LanguageProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
